import { useState } from 'react';
import { useQuery } from 'react-query';
import { reportsApi, projectsApi } from '@/services/api';
import { X, PartyPopper } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';

interface PartialAttempt {
  id: string;
  url: string;
  method: string;
  queryParams: any;
  missingFields: string[];
  endpoint: 'complete' | 'terminate' | 'quota' | 'security';
  projectId?: string;
  maskedId?: string;
  ipAddress?: string;
  userAgent?: string;
  errorMessage?: string;
  createdAt: string;
}

const FailedAttemptsPage = () => {
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('');
  const [limit, setLimit] = useState<number>(50);
  const [selectedAttempt, setSelectedAttempt] = useState<PartialAttempt | null>(null);

  // Fetch failed attempts
  const { data: failedAttemptsData, isLoading, refetch } = useQuery(
    ['failedAttempts', selectedProject, selectedEndpoint, limit],
    () => reportsApi.getFailedAttempts({
      projectId: selectedProject || undefined,
      endpoint: selectedEndpoint as any || undefined,
      limit
    }),
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Failed attempts error:', error);
      },
      retry: false,
    }
  );

  // Projects List for dropdown
  const { data: projectsList } = useQuery(
    'projects',
    () => projectsApi.getAll(),
    {
      select: (data: any) => data.data || [],
      onError: (error) => {
        console.error('Projects list error:', error);
      },
      retry: false,
    }
  );

  const attempts = failedAttemptsData?.data?.attempts || [];
  const total = failedAttemptsData?.data?.total || 0;

  const getEndpointBadgeColor = (endpoint: string) => {
    switch (endpoint) {
      case 'complete': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'terminate': return 'bg-red-50 text-red-700 border border-red-200';
      case 'quota': return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'security': return 'bg-purple-50 text-purple-700 border border-purple-200';
      default: return 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Failed Tracking Attempts</h1>
        <p className="text-[#6B7280] text-[15px]">
          Monitor and troubleshoot failed or incomplete tracking attempts
        </p>
      </div>

      {/* Filters */}
      <div className="md-card p-6 animate-fade-in-up stagger-1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
              Project
            </label>
            <div className="md-outlined-textfield">
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full"
              >
                <option value="">All Projects</option>
                {(projectsList || []).map((project: any) => (
                  <option key={project.id} value={project.projectId}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
              Endpoint
            </label>
            <div className="md-outlined-textfield">
              <select
                value={selectedEndpoint}
                onChange={(e) => setSelectedEndpoint(e.target.value)}
                className="w-full"
              >
                <option value="">All Endpoints</option>
                <option value="complete">Complete</option>
                <option value="terminate">Terminate</option>
                <option value="quota">Quota Full</option>
                <option value="security">Security</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
              Limit
            </label>
            <div className="md-outlined-textfield">
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => refetch()}
              className="md-btn md-btn-primary w-full text-[13px]"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 animate-fade-in-up stagger-2">
        <div className="md-card p-6">
          <div className="text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Total Failed Attempts</div>
          <div className="mt-2 text-3xl font-bold text-[#1A1F2B]">{total}</div>
        </div>
        <div className="md-card p-6">
          <div className="text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Missing Data</div>
          <div className="mt-2 text-3xl font-bold text-orange-600">
            {attempts.filter((a: PartialAttempt) => a.missingFields.length > 0).length}
          </div>
        </div>
        <div className="md-card p-6">
          <div className="text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Database Errors</div>
          <div className="mt-2 text-3xl font-bold text-[#EF4444]">
            {attempts.filter((a: PartialAttempt) => a.errorMessage && a.errorMessage.includes('failed')).length}
          </div>
        </div>
        <div className="md-card p-6">
          <div className="text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Last 30 Minutes</div>
          <div className="mt-2 text-3xl font-bold text-[#1A1F2B]">
            {attempts.filter((a: PartialAttempt) =>
              new Date(a.createdAt).getTime() > Date.now() - 30 * 60 * 1000
            ).length}
          </div>
        </div>
      </div>

      {/* Failed Attempts Table */}
      <div className="md-card overflow-hidden animate-fade-in-up stagger-3">
        <div className="px-6 py-4 border-b border-[#ECEEF1]">
          <h2 className="text-lg font-bold font-heading text-[#1A1F2B]">Recent Failed Attempts</h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-[#6B7280]">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#18A5F9] mx-auto mb-3"></div>
            Loading failed attempts...
          </div>
        ) : attempts.length === 0 ? (
          <div className="p-8 text-center text-[#6B7280] flex items-center justify-center gap-2 font-medium">
            No failed attempts found. This is good news! <PartyPopper className="w-5 h-5 text-[#18A5F9]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Timestamp</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Endpoint</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Project ID</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Missing Fields</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Error</th>
                  <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((attempt: PartialAttempt) => (
                  <tr key={attempt.id} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap text-sm text-[#374151]">
                      {formatDateTime(attempt.createdAt)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getEndpointBadgeColor(attempt.endpoint)}`}>
                        {attempt.endpoint}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm text-[#374151] font-mono">
                      {attempt.projectId || <span className="text-[#9CA3AF]">&mdash;</span>}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">
                      {attempt.missingFields.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {attempt.missingFields.map((field, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full font-semibold border border-red-200">
                              {field}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#9CA3AF]">&mdash;</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#6B7280]">
                      {attempt.errorMessage ? (
                        <span className="truncate block max-w-xs" title={attempt.errorMessage}>
                          {attempt.errorMessage}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">&mdash;</span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedAttempt(attempt)}
                        className="text-[#18A5F9] hover:text-[#0D8FDE] font-semibold text-[13px] transition-colors"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedAttempt && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
            <div className="px-6 py-4 border-b border-[#ECEEF1] flex justify-between items-center">
              <h3 className="text-lg font-bold font-heading text-[#1A1F2B]">Failed Attempt Details</h3>
              <button
                onClick={() => setSelectedAttempt(null)}
                className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">URL</h4>
                <code className="block p-3 bg-[#F9FAFB] rounded-xl text-sm border border-[#ECEEF1] font-mono text-[#374151]">{selectedAttempt.url}</code>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Method</h4>
                  <p className="text-sm font-semibold text-[#374151]">{selectedAttempt.method}</p>
                </div>
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Endpoint</h4>
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getEndpointBadgeColor(selectedAttempt.endpoint)}`}>
                    {selectedAttempt.endpoint}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Project ID</h4>
                  <p className="text-sm font-semibold text-[#374151]">{selectedAttempt.projectId || '\u2014'}</p>
                </div>
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Masked ID</h4>
                  <p className="text-sm font-semibold text-[#374151] font-mono">{selectedAttempt.maskedId || '\u2014'}</p>
                </div>
              </div>

              <div>
                <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Missing Fields</h4>
                {selectedAttempt.missingFields.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedAttempt.missingFields.map((field, idx) => (
                      <span key={idx} className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full font-semibold border border-red-200">
                        {field}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#9CA3AF]">None</p>
                )}
              </div>

              <div>
                <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Query Parameters</h4>
                <pre className="p-3 bg-[#F9FAFB] rounded-xl text-xs overflow-x-auto border border-[#ECEEF1] font-mono text-[#6B7280]">
                  {JSON.stringify(selectedAttempt.queryParams, null, 2)}
                </pre>
              </div>

              {selectedAttempt.errorMessage && (
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Error Message</h4>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                    {selectedAttempt.errorMessage}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">IP Address</h4>
                  <p className="text-sm font-semibold text-[#374151] font-mono">{selectedAttempt.ipAddress || '\u2014'}</p>
                </div>
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">Timestamp</h4>
                  <p className="text-sm font-semibold text-[#374151]">{formatDateTime(selectedAttempt.createdAt)}</p>
                </div>
              </div>

              {selectedAttempt.userAgent && (
                <div>
                  <h4 className="text-[13px] font-bold text-[#9CA3AF] mb-2 uppercase tracking-wider">User Agent</h4>
                  <p className="text-xs text-[#6B7280] break-all">{selectedAttempt.userAgent}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#ECEEF1] flex justify-end">
              <button
                onClick={() => setSelectedAttempt(null)}
                className="md-btn md-btn-outlined text-[13px]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailedAttemptsPage;
